import { Matrix4, Vector3, Vector3Like } from 'three'

export function getPositionDiffs(positions: Vector3Like[]) {
  const xMin = positions.reduce((min, position) => {
    return position.x < min ? position.x : min
  }, Infinity)
  const xMax = positions.reduce((max, position) => {
    return position.x > max ? position.x : max
  }, -Infinity)
  const yMin = positions.reduce((min, position) => {
    return position.y < min ? position.y : min
  }, Infinity)
  const yMax = positions.reduce((max, position) => {
    return position.y > max ? position.y : max
  }, -Infinity)

  const xDiff = xMax - xMin
  const yDiff = yMax - yMin

  return {
    xDiff,
    xMin,
    xMax,
    yDiff,
    yMin,
    yMax,
  }
}

export function transformMatrix(
  pivot: Vector3Like,
  dragging: Vector3Like,
  delta: Vector3Like,
  shiftHeld: boolean
): Matrix4 {
  const trueDelta = shiftHeld
    ? new Vector3().copy(delta).projectOnVector(new Vector3().subVectors(dragging, pivot))
    : delta

  const draggingFromPivot = new Vector3().copy(dragging).sub(pivot)
  const pxVector = draggingFromPivot.clone().add(trueDelta)
  const xVector = new Vector3().copy(pxVector).divide(draggingFromPivot)
  if (isNaN(xVector.x)) xVector.setX(1)
  if (!isFinite(xVector.x)) xVector.setX(xVector.x < 0 ? 1 : 1)
  if (isNaN(xVector.y)) xVector.setY(1)
  if (!isFinite(xVector.y)) xVector.setY(xVector.y < 0 ? 1 : 1)
  if (isNaN(xVector.z)) xVector.setZ(1)
  if (!isFinite(xVector.z)) xVector.setZ(xVector.z < 0 ? 1 : 1)

  const goToOrigin = new Matrix4().makeTranslation(-pivot.x, -pivot.y, -pivot.z)
  const rescale = new Matrix4().makeScale(xVector.x, xVector.y, xVector.z)
  const goFromOrigin = new Matrix4().makeTranslation(pivot.x, pivot.y, pivot.z)

  const mtx = new Matrix4()

  mtx.multiply(goFromOrigin)
  mtx.multiply(rescale)
  mtx.multiply(goToOrigin)

  return mtx
}

export function apply(vector: Vector3Like, matrix: Matrix4): Vector3 {
  return new Vector3().copy(vector).applyMatrix4(matrix)
}
